import React from "react"
import { useTranslation } from "react-i18next"

import { useAuthentication } from "@/contexts"
import { nonAdminUserRoles, userRoles } from "@/models/user-role"
import type { Option } from "@/nzds/inputs/inputs.types"

import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"

export function useRoleSelectProps() {
  const { t } = useTranslation("common")
  const { isAdminWriterUser } = useAuthentication()

  const availableRoles = isAdminWriterUser ? userRoles : nonAdminUserRoles

  const roleOptions = availableRoles.map((role) => ({
    label: t(role.name),
    value: role.id,
  }))

  const roleDescriptionsByValue = React.useMemo(
    () =>
      new Map<string, string>(
        availableRoles.map((role) => [role.id, t(role.description)])
      ),
    [availableRoles, t]
  )

  const renderRoleOption = React.useCallback(
    (option: Option) => (
      <Box>
        <Typography variant="body1">{option.label}</Typography>
        {option.value && roleDescriptionsByValue.has(String(option.value)) && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {roleDescriptionsByValue.get(String(option.value))}
          </Typography>
        )}
      </Box>
    ),
    [roleDescriptionsByValue]
  )

  return { roleOptions, renderRoleOption }
}
